// Sales discounts (ADR-052) — data access for Sales → Discounts (PX-SAL-330).
//
//   sales_discount_page(company, scope, term, after, limit)  keyset on
//       (customer code, item code — range first, minimum, valid from, id)
//   save_sales_discount(id, company, params) / delete_sales_discount(id)

import { infiniteQueryOptions } from "@tanstack/react-query";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";

export type DiscountScope = "current" | "all";
export const discountScopes: DiscountScope[] = ["current", "all"];
export const isDiscountScope = (v: unknown): v is DiscountScope => v === "current" || v === "all";

export interface DiscountRow {
  id: string;
  customer_id: string;
  customer_code: string;
  customer_name: string;
  item_id: string | null;
  item_code: string | null;
  item_description: string | null;
  discount_pct: string | number;
  min_qty: string | number;
  base_uom_code: string;
  valid_from: string;
  valid_to: string | null;
  cursor: Record<string, unknown>;
}

export interface DiscountInput {
  customer_id?: string;
  item_id?: string | null;
  discount_pct: string;
  min_qty: string;
  valid_from: string;
  valid_to: string | null;
}

export interface ListParams {
  companyId: string;
  scope: DiscountScope;
  q: string;
}

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

export const discountKeys = {
  all: ["sales", "discounts"] as const,
  list: (p: ListParams) => [...discountKeys.all, "list", p] as const,
};

export async function listDiscounts(p: ListParams, cursor: string | null, signal: AbortSignal, size = 50): Promise<Page<DiscountRow>> {
  if (isDemoMode) return { rows: [], nextCursor: null };
  const { data, error } = await db()
    .rpc("sales_discount_page", {
      p_company_id: p.companyId,
      p_scope: p.scope,
      p_term: p.q.trim() || null,
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = (data ?? []) as DiscountRow[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify(last.cursor) : null };
}

export function discountListQuery(p: ListParams) {
  return infiniteQueryOptions({
    queryKey: discountKeys.list(p),
    queryFn: ({ pageParam, signal }) => listDiscounts(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<DiscountRow>) => last.nextCursor,
    staleTime: 60_000,
    enabled: !!p.companyId,
  });
}

export async function saveDiscount(id: string | null, companyId: string, input: DiscountInput) {
  const { data, error } = await db().rpc("save_sales_discount", { p_id: id, p_company_id: companyId, p_params: input });
  if (error) throw error;
  return data as string;
}

export async function deleteDiscount(id: string) {
  const { error } = await db().rpc("delete_sales_discount", { p_id: id });
  if (error) throw error;
}
